import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Phone, MessageCircle, Facebook, Instagram, Music, MapPin, Clock, Shield, Car } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-slate-50 to-white border-t border-border">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img 
                src="./images/IMG-20251012-WA0003.jpg" 
                alt="Glow by Precious Logo - With Nature's Touch"
                className="h-12 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Redefining beauty, health, and wellness in Polokwane with premium treatments and personalized care.
            </p>
            <div className="flex gap-3">
              <a href="https://facebook.com/glowbyprecious" target="_blank" rel="noopener noreferrer" 
                 className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-smooth">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="https://instagram.com/glowbyprecious1" target="_blank" rel="noopener noreferrer"
                 className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-smooth">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="https://tiktok.com/@glowbyprecious" target="_blank" rel="noopener noreferrer"
                 className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-smooth">
                <Music className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground">Quick Links</h4>
            <nav className="space-y-2">
              <Link to="/about" className="block text-sm text-muted-foreground hover:text-primary transition-smooth">About Us</Link>
              <Link to="/services" className="block text-sm text-muted-foreground hover:text-primary transition-smooth">Our Services</Link>
              <Link to="/gallery" className="block text-sm text-muted-foreground hover:text-primary transition-smooth">Gallery</Link>
              <Link to="/packages" className="block text-sm text-muted-foreground hover:text-primary transition-smooth">Packages</Link>
              <Link to="/faq" className="block text-sm text-muted-foreground hover:text-primary transition-smooth">FAQ</Link>
              <Link to="/contact" className="block text-sm text-muted-foreground hover:text-primary transition-smooth">Contact</Link>
            </nav>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground">Our Services</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>Body Contouring</p>
              <p>Slimming & Glow Injections</p>
              <p>IV Drip Therapy</p>
              <p>Therapeutic Massage</p>
              <p>Butt Vacuum</p>
              <p>Medical Weight Loss</p>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground">Contact Info</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                <div className="text-sm text-muted-foreground">
                  <p>100 Market Street, Office No. 12</p>
                  <p>Amie Bava Square</p>
                  <p>Polokwane, South Africa</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-primary" />
                <a href="tel:0695523598" className="text-sm text-muted-foreground hover:text-primary transition-smooth">
                  069 552 3598
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-primary" />
                <div className="text-sm text-muted-foreground">
                  <p>Mon-Fri: 9AM-5PM</p>
                  <p>Sat: 9AM-1PM • Sun: Closed</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Car className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Secured Parking Available</span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="text-center space-y-4">
            <h3 className="text-xl font-semibold text-foreground">Ready to Start Your Glow Journey?</h3>
            <p className="text-muted-foreground">Book your consultation today and discover your transformation</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button className="gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300 w-full sm:w-auto">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp Us Now
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-smooth w-full sm:w-auto">
                  <Phone className="h-4 w-4 mr-2" />
                  Call to Book
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <p>&copy; 2024 Glow by Precious. All rights reserved.</p>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span>POPIA Compliant</span>
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <Link to="/policies" className="text-muted-foreground hover:text-primary transition-smooth">Privacy Policy</Link>
            <Link to="/policies" className="text-muted-foreground hover:text-primary transition-smooth">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;